// Permanent template store at /templates/{templateKey} — independent of
// /tournaments and untouched by demo TTL cleanup, full-tournament close,
// or any other lifecycle. The AI assistant's COPY_TOURNAMENT /
// SETUP_COMPLETE_TOURNAMENT resolvers check here FIRST, falling back to
// live /tournaments scans only if no usable template exists.
//
// Template shape mirrors a real tournament minus the live-data nodes that
// don't belong to a template (scores, results, aiLog, announcements,
// tracking, photos, calcutta bids/winners/owners, per-team scores).
// Everything else is preserved verbatim so a new tournament copied from a
// template comes out fully configured: flights, teams, tee times,
// contests, registrations with qualifying scores, calcutta settings,
// photo settings, TV settings, brackets, qualifying state.

import { get, push, ref, remove, set, update } from 'firebase/database'
import { db } from './firebase.js'
import { ensureTournamentInfo } from './data.js'

// Lowercase, alphanumeric + dashes only — RTDB keys can't contain `.#$/[]`
// and lowercase saves directors from "Pbcc 4 Ball" vs "PBCC 4-Ball" key
// collisions.
export function slugifyTemplateName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

// Build the template tree from an in-memory tournament object. Same
// strip rules as handleCopyTournament so a template is a portable copy
// of the tournament's CONFIG only.
export function tournamentToTemplate(tournament) {
  if (!tournament || typeof tournament !== 'object') return null
  const t = JSON.parse(JSON.stringify(tournament))
  // Top-level drops — live data only, never belongs to a template.
  delete t.scores
  delete t.results
  delete t.aiLog
  delete t.announcements
  delete t.tracking
  delete t.photos
  // Per-team scores under flights.
  if (t.flights && typeof t.flights === 'object') {
    for (const fid of Object.keys(t.flights)) {
      const f = t.flights[fid]
      if (f && typeof f === 'object' && f.teams && typeof f.teams === 'object') {
        for (const tid of Object.keys(f.teams)) {
          if (f.teams[tid] && f.teams[tid].scores) delete f.teams[tid].scores
        }
      }
    }
  }
  // Calcutta — keep settings only, drop auction state.
  if (t.calcutta && typeof t.calcutta === 'object') {
    if (t.calcutta.settings) {
      t.calcutta = { settings: { ...t.calcutta.settings, auctionOpen: false } }
    } else {
      delete t.calcutta
    }
  }
  // Info — clear runtime status flags. Keep name/date/format as the
  // template's identity; the AI copy will override them when applied.
  if (t.info && typeof t.info === 'object') {
    delete t.info.status
    delete t.info.closedAt
    delete t.info.scoringEnabled
    delete t.info.isDemo
    delete t.info.isTestStation
    delete t.info.testStationTile
    delete t.info.testStationCreatedAt
    delete t.info.demoCreatedAt
    delete t.info.practice
  }
  return t
}

// Save (or overwrite) a template under /templates/{slug}. Returns the
// slug actually used so the caller can confirm.
export async function saveTemplate(name, tournament, meta = {}) {
  const slug = slugifyTemplateName(name)
  if (!slug) throw new Error('Template name is required.')
  const tree = tournamentToTemplate(tournament)
  if (!tree) throw new Error('Tournament data was empty.')
  tree.meta = {
    ...(tree.meta || {}),
    templateName: name.trim(),
    savedAt: Date.now(),
    savedBy: meta.savedBy || null,
    sourceTournamentId: meta.sourceTournamentId || null,
  }
  await set(ref(db, `templates/${slug}`), tree)
  return { slug, name: name.trim() }
}

export async function listTemplates() {
  const snap = await get(ref(db, 'templates'))
  const all = snap.val() || {}
  return Object.entries(all).map(([slug, v]) => ({
    slug,
    name: v?.meta?.templateName || slug,
    format: v?.info?.format || null,
    savedAt: v?.meta?.savedAt || null,
    flightCount: Object.keys(v?.flights || {}).filter((k) => !k.startsWith('_')).length,
    teamCount: Object.values(v?.flights || {})
      .reduce((n, f) => n + (f && f.teams ? Object.keys(f.teams).length : 0), 0),
    hasTeeTimes: !!(v?.teeTimes && Object.keys(v.teeTimes).length),
    hasRegistrations: !!v?.registrations,
  }))
}

export async function loadTemplate(slug) {
  const snap = await get(ref(db, `templates/${slug}`))
  return snap.val()
}

export async function deleteTemplate(slug) {
  await remove(ref(db, `templates/${slug}`))
}

// Direct copy from /templates/<path> into a brand-new /tournaments/{id}.
// Pure Firebase read+write — does NOT go through aiActionExecutor or any
// AI path. Used by the "Set Up Manually" button on the demo launcher.
//
// EMPTY-SHELL COPY by design: the director picked "Set Up Manually" to
// configure flights, tee times, and brackets from scratch, so the only
// things carried forward from the template are the player roster
// (registrations) and the 4 fields needed to identify the tournament
// (name, course, date, format). Everything else — flights, teeTimes,
// brackets, calcutta settings, contests, photoSettings, qualifying
// state, etc. — is deliberately dropped so the director lands in a
// blank tournament with only the field loaded. The AI "Set Up with AI"
// path uses its own deep-copy (handleCopyTournament in
// aiActionExecutor) and still copies everything.
export async function copyTemplateToTournament(templatePath, { name, date } = {}) {
  const snap = await get(ref(db, templatePath))
  const template = snap.val()
  if (!template || typeof template !== 'object') {
    throw new Error(`Template not found at /${templatePath}`)
  }
  const today = new Date().toISOString().slice(0, 10)
  // Build the new tournament tree from scratch — only the registrations
  // and a minimal info block. No spread of the template at the root, so
  // flights/teeTimes/brackets/calcutta/contests/etc. simply don't make
  // it into the new node.
  const tree = {
    info: {
      name: name || template.info?.name || 'Tournament',
      course: template.info?.course || '',
      date: date || template.info?.date || today,
      format: template.info?.format || '',
      status: 'open',
      createdAt: Date.now(),
      closedAt: null,
    },
  }
  // Registrations (the 96-team field) — verbatim if present. Templates
  // may not include registrations; copy only if the template has them.
  if (template.registrations && typeof template.registrations === 'object') {
    // Deep clone so the in-memory RTDB snapshot doesn't get mutated.
    tree.registrations = JSON.parse(JSON.stringify(template.registrations))
  }
  ensureTournamentInfo(tree.info)
  const newRef = push(ref(db, 'tournaments'))
  const newId = newRef.key
  await set(newRef, tree)
  return newId
}
